/**
 * 붙여넣은 그림을 가르는 **순수 판정** — tiptap·DOM 편집기 없이 단독으로 시험된다.
 *
 * ## 클립보드가 그림을 주는 꼴은 «셋»이다 — 하나를 빠뜨리면 그 경로의 그림만 조용히 사라진다
 *
 * ```
 * ① clipboardData.files        바이트가 손에 있다        → 바로 올린다
 * ② <img src="data:…">         HTML 안에 base64 로 박혀  → 해독해서 올린다
 *                              ⛔ 그냥 두면 본문 길이 제한(서버 10,000자)에 걸려 저장이
 *                                 거부되거나, 잘려서 저장 뒤 사라진다
 * ③ <img src="http(s)://…">    남의 서버를 가리킨다      → 서버 프록시가 필요(이 판에 없다)
 *                              ⛔ 그대로 남기면 ⓐ 그쪽이 지우면 깨지고 ⓑ 우리 사용자가
 *                                 글을 볼 때마다 남의 서버에 흔적을 남긴다
 * ```
 *
 * ⚠ **워드에서 복사하면 대개 ② 라는 관측이 있으나 우리가 잰 것은 아니다**(2026-09-23 ·
 *   이 세션에는 브라우저가 없다). 그래서 셋을 다 다룬다 — 「어느 하나일 것이다」에
 *   기대지 않는다.
 */

import {
  IMAGE_FAILED_REASON_REMOTE,
  IMAGE_FAILED_REASON_UNDECODABLE,
} from './editor-messages';

/** 우리가 다루는 그림 MIME 허용목록. 서버 presign 화이트리스트와 같은 범위로 좁게 둔다. */
const ALLOWED_IMAGE_MIME = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/svg+xml',
]);

/**
 * ⛔ SVG 는 **스크립트를 품을 수 있다**. 바이트를 그대로 올려 `<img src>` 로만 보여 주면
 *   브라우저가 SVG 안의 스크립트를 실행하지 않지만, 같은 파일을 새 탭으로 열면 실행된다.
 *   서버가 `Content-Disposition: attachment` 로 주므로 현재는 막히지만, 이 판에서는
 *   **붙여넣기 경로에서 아예 받지 않는다** — 이득(워드 그림은 SVG 로 오지 않는다)보다
 *   위험이 크다. 조건 ④(보안).
 */
const REFUSED_IMAGE_MIME = new Set(['image/svg+xml']);

export function isAllowedImageMime(mime: string): boolean {
  const m = mime.toLowerCase().split(';')[0]?.trim() ?? '';
  return ALLOWED_IMAGE_MIME.has(m) && !REFUSED_IMAGE_MIME.has(m);
}

// ── ① clipboardData.files ───────────────────────────────────────────────────

/**
 * 클립보드/드롭에서 **바이트를 손에 든** 그림만 뽑는다.
 *
 * `files` 와 `items` 를 **둘 다** 본다. 브라우저에 따라 한쪽만 채워 주는 일이 있고
 * (Safari 는 `items`, 옛 Firefox 는 `files`), 워드처럼 그림과 HTML 을 함께 주는 경우
 * `items` 에는 `string` 항목도 섞여 있어 `kind === 'file'` 로 걸러야 한다.
 */
export function collectClipboardImageFiles(dt: DataTransfer | null | undefined): File[] {
  if (!dt) return [];
  const out: File[] = [];
  const seen = new Set<string>();
  const push = (f: File | null) => {
    if (!f) return;
    if (!isAllowedImageMime(f.type)) return;
    // 같은 그림이 files 와 items 에 두 번 나타나는 것을 막는다.
    const key = `${f.name}|${f.size}|${f.type}|${f.lastModified}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(f);
  };
  const files = dt.files;
  if (files) for (let i = 0; i < files.length; i += 1) push(files.item(i));
  const items = dt.items;
  if (items) {
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      if (!it || it.kind !== 'file') continue;
      push(it.getAsFile());
    }
  }
  return out;
}

// ── ② data: URL ─────────────────────────────────────────────────────────────

const DATA_URL_RE = /^data:([^;,]+)(;[^,]*)?,(.*)$/s;

/**
 * `data:` URL → `File`. 해독할 수 없으면 `null`(호출자가 실패 표시를 남긴다).
 *
 * ⚠ base64 가 아닌 `data:`(퍼센트 인코딩 본문)도 온다. 그때 **`decodeURIComponent` 를
 *   쓰면 안 된다** — 그것은 결과를 UTF-8 글자로 읽으려 들어 그림 바이트(`%FF%D8` …)에
 *   `URIError` 를 던진다(실측). 바이트 단위로 풀어야 한다.
 * ⚠ `atob` 는 잘린 base64 에 예외를 던진다. 던지게 두면 붙여넣기 하나가 편집기 전체를
 *   죽이므로 여기서 잡아 `null` 로 눕힌다.
 */
export function dataUrlToFile(dataUrl: string, nameHint: string): File | null {
  const m = DATA_URL_RE.exec(dataUrl);
  if (!m) return null;
  const mime = (m[1] ?? '').toLowerCase();
  const isBase64 = (m[2] ?? '').includes('base64');
  const payload = m[3] ?? '';
  if (!isAllowedImageMime(mime)) return null;
  let bytes: Uint8Array;
  try {
    if (isBase64) {
      const bin = atob(payload);
      bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    } else {
      bytes = percentDecodeToBytes(payload);
    }
  } catch {
    return null;
  }
  if (bytes.length === 0) return null;
  const ext = mime.split('/')[1]?.replace('+xml', '') ?? 'png';
  return new File([bytes], `${nameHint}.${ext}`, { type: mime });
}

/** `%FF` 꼴을 **바이트 그대로** 푼다 — 글자로 읽지 않는다(그래서 그림에도 안전하다). */
function percentDecodeToBytes(s: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i] as string;
    if (ch === '%' && i + 2 < s.length) {
      const hex = s.slice(i + 1, i + 3);
      if (/^[0-9a-f]{2}$/i.test(hex)) {
        out.push(Number.parseInt(hex, 16));
        i += 2;
        continue;
      }
    }
    out.push(ch.charCodeAt(0) & 0xff);
  }
  return new Uint8Array(out);
}

// ── ③ 판정 ──────────────────────────────────────────────────────────────────

/** 이 주소가 이미 «우리 것»인가 — 앱 상대 주소는 다시 올릴 필요가 없다. */
export function isAppRelativeSrc(src: string): boolean {
  return src.startsWith('/') && !src.startsWith('//');
}

export type PastedImagePlan =
  /** 해독해서 올린다(② data:). */
  | { kind: 'upload'; src: string; file: File }
  /** 이미 우리 주소다 — 그대로 둔다. */
  | { kind: 'keep'; src: string }
  /**
   * 올릴 수 없다 — **지워지지 않는 실패 표시**를 남긴다.
   * `reason` 은 사용자에게 그대로 보여 줄 까닭이다.
   */
  | { kind: 'fail'; src: string; reason: string };

/**
 * 붙여넣은 `<img src>` 목록 → 무엇을 할지.
 *
 * ★ 판정이 **여기 한 곳**에 있다. 편집기 쪽 드라이버는 이 결과를 실행만 하므로,
 *   시험이 이 함수를 직접 부르면 「화면 시험이 모듈을 통째로 mock 해 판정이 안 재지는」
 *   함정을 피할 수 있다.
 */
export function planPastedImages(srcs: readonly string[]): PastedImagePlan[] {
  return srcs.map((src, i) => {
    if (src.startsWith('data:')) {
      const file = dataUrlToFile(src, `pasted-image-${i + 1}`);
      return file
        ? { kind: 'upload', src, file }
        : { kind: 'fail', src, reason: IMAGE_FAILED_REASON_UNDECODABLE };
    }
    if (isAppRelativeSrc(src)) return { kind: 'keep', src };
    if (/^https?:\/\//i.test(src)) {
      // ③ 바깥 주소 — 서버 프록시가 붙기 전까지는 «가져올 수 없다».
      //    ⛔ 그대로 남기지 않는다(바깥 주소를 본문에 남기지 않는다는 원칙).
      return { kind: 'fail', src, reason: IMAGE_FAILED_REASON_REMOTE };
    }
    // blob: · file: · cid: 등 — 저장하면 반드시 깨지는 주소다.
    return { kind: 'fail', src, reason: IMAGE_FAILED_REASON_UNDECODABLE };
  });
}

/** 붙여넣은 HTML 조각에서 `<img src>` 를 순서대로 뽑는다(브라우저 DOM 파서 사용). */
export function extractImageSrcs(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.querySelectorAll('img'))
    .map((el) => el.getAttribute('src') ?? '')
    .filter((s) => s.length > 0);
}
